import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { formatDuration } from '../utils/formatUtils';
import {
    Container,
    Typography,
    Box,
    Card,
    CardContent,
    Button,
    Grid,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DragIcon from '@mui/icons-material/DragIndicator';

function SessionDetail() {
    const { sessionId } = useParams();
    const id = sessionId;
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [exerciseToDelete, setExerciseToDelete] = useState(null);

    const fetchSession = useCallback(async () => {
        try {
            const response = await apiClient.get('/sessions');
            const found = Array.isArray(response.data)
                ? response.data.find(s => String(s.id) === String(id))
                : response.data?.sessions?.find(s => String(s.id) === String(id));
            if (found) setSession(found);
        } catch (error) {
            console.error('Erreur lors de la récupération de la session:', error);
        }
    }, [id]);

    useEffect(() => {
        fetchSession();
    }, [fetchSession]);

    const handleDeleteExercise = async (exerciseId) => {
        try {
            await apiClient.delete(`/sessions/${id}/exercises/${exerciseId}`);
            fetchSession();
        } catch (error) {
            console.error('Erreur lors de la suppression de l\'exercice:', error);
        }
    };

    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        // Si la destination est la même, ne rien faire
        if (sourceIndex === destinationIndex) return;

        const items = Array.from(session.exercises);
        const [reorderedItem] = items.splice(sourceIndex, 1);
        items.splice(destinationIndex, 0, reorderedItem);

        // Mettre à jour l'état local immédiatement
        setSession(prev => ({
            ...prev,
            exercises: items
        }));

        // Mettre à jour l'ordre côté backend
        try {
            await apiClient.put(`/sessions/${id}/exercises/order`, {
                exercises: items.map((ex, idx) => ({ id: ex.id, order: idx }))
            });
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'ordre des exercices:', error);
            // En cas d'erreur, on restaure l'état précédent
            fetchSession();
        }
    };

    // Collecte tous les matériels uniques de tous les exercices de la session.
    // Le matériel renseigné au niveau de l'exercice est la source de vérité ;
    // le champ "MATÉRIEL" de séance n'est pas fiable (matériel parasite).
    const collectUniqueMaterials = () => {
        if (!session || !session.exercises) return [];

        const materials = [];

        for (const exercise of session.exercises) {
            if (exercise.materiel && Array.isArray(exercise.materiel) && exercise.materiel.length > 0) {
                materials.push(...exercise.materiel);
            }
        }

        // Supprimer les doublons et trier pour un affichage cohérent
        return [...new Set(materials)].sort();
    };

    const uniqueMaterials = collectUniqueMaterials();

    if (!session) {
        return <Typography>Chargement...</Typography>;
    }

    return (
        <Container>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    {session.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    {session.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Durée: {formatDuration(session.duration)}
                </Typography>
                {uniqueMaterials.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3e0', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#e65100' }}>
                            Matériel nécessaire : {uniqueMaterials.join(', ')}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5">Exercices</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate(`/sessions/${id}/exercises/new`)}
                >
                    Ajouter un exercice
                </Button>
            </Box>

            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="exercises-droppable">
                    {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} style={{ width: '100%' }}>
                            <Grid container spacing={3} sx={{ width: '100%' }}>
                                {session.exercises && session.exercises.map((exercise, index) => (
                                    <Draggable key={String(exercise.id)} draggableId={String(exercise.id)} index={index}>
                                        {(provided, snapshot) => (
                                            <Grid item xs={12} md={12} lg={12} ref={provided.innerRef} {...provided.draggableProps} style={{
                                                ...provided.draggableProps.style,
                                                transform: provided.draggableProps.style?.transform,
                                                width: '100%',
                                            }}>
                                                <Card sx={{
                                                    backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'white',
                                                    boxShadow: snapshot.isDragging ? 3 : 1,
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        boxShadow: 2,
                                                    },
                                                }}>
                                                    <CardContent>
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                            <Box>
                                                                <Typography variant="body2">{exercise.fake_title}</Typography>
                                                                <Typography variant="h6">{exercise.name}</Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {exercise.text_moteur}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {exercise.text_cognitif}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Durée: {formatDuration(exercise.duration)}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                                <Box
                                                                    {...provided.dragHandleProps}
                                                                    sx={{
                                                                        cursor: 'grab',
                                                                        mr: 1,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        color: snapshot.isDragging ? 'primary.main' : 'text.secondary',
                                                                        '&:active': {
                                                                            cursor: 'grabbing',
                                                                        },
                                                                    }}
                                                                >
                                                                    <DragIcon />
                                                                </Box>
                                                                <IconButton onClick={() => navigate(`/sessions/${id}/exercises/${exercise.id}/edit`)}>
                                                                    <EditIcon />
                                                                </IconButton>
                                                                <IconButton onClick={() => { setExerciseToDelete(exercise.id); setOpenDialog(true); }}>
                                                                    <DeleteIcon />
                                                                </IconButton>
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    sx={{ ml: 1 }}
                                                                    onClick={() => navigate(`/sessions/${id}/exercises/${exercise.id}`)}
                                                                >
                                                                    Voir détail
                                                                </Button>
                                                            </Box>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </Grid>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    Êtes-vous sûr de vouloir supprimer cet exercice ?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Annuler</Button>
                    <Button onClick={() => {
                        handleDeleteExercise(exerciseToDelete);
                        setOpenDialog(false);
                    }} color="error">
                        Supprimer
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default SessionDetail; 